/**
 * Audio Notification Utility
 * Handles playing audio notifications for CAD events
 */

class AudioNotificationService {
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private isEnabled: boolean = true;

  constructor() {
    // Preload audio files
    this.preloadAudio('newWaitingIncident', '/NewWaitingIncident.wav');
    this.preloadAudio('callAssignedToMe', '/CallAssignedToMe.wav');
    this.preloadAudio('phoneRing', '/PhoneRing.mp3');
    this.preloadAudio('mediumTone', '/MediumTone.mp3');
    this.preloadAudio('priorityHigh', '/PriorityHigh.mp3');
    this.preloadAudio('longTone', '/LongTone.mp3');
  }

  /**
   * Preload an audio file into the cache
   */
  private preloadAudio(key: string, url: string): void {
    try {
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.volume = 0.7; // Default volume at 70%
      this.audioCache.set(key, audio);
      
      // Handle load errors
      audio.addEventListener('error', (e) => {
        console.error(`[AudioNotification] Failed to load audio: ${url}`, e);
      });
    } catch (error) {
      console.error(`[AudioNotification] Error preloading audio ${key}:`, error);
    }
  }

  /**
   * Play an audio notification
   */
  private async playAudio(key: string, volumeOverride?: number): Promise<void> {
    console.log(`[AudioNotification] 🔊 Attempting to play: ${key}`);
    
    if (!this.isEnabled) {
      console.log(`[AudioNotification] ❌ Skipping ${key} - notifications disabled`);
      return;
    }

    const audio = this.audioCache.get(key);
    if (!audio) {
      console.warn(`[AudioNotification] ❌ Audio not found in cache: ${key}`);
      console.warn(`[AudioNotification] Available audio files:`, Array.from(this.audioCache.keys()));
      return;
    }

    console.log(`[AudioNotification] Audio state for ${key}:`, {
      readyState: audio.readyState,
      paused: audio.paused,
      duration: audio.duration,
      volume: audio.volume,
      src: audio.src
    });

    try {
      // Store original volume and apply override if provided
      const originalVolume = audio.volume;
      if (volumeOverride !== undefined) {
        audio.volume = volumeOverride;
      }
      
      // Reset the audio to the beginning if it's already playing
      audio.currentTime = 0;
      
      // Play the audio
      console.log(`[AudioNotification] ▶️ Calling play() for ${key}...`);
      await audio.play();
      console.log(`[AudioNotification] ✅ Successfully playing: ${key}`);
      
      // Restore original volume after audio finishes playing
      // For phoneRing, keep the reduced volume permanently (don't restore)
      if (volumeOverride !== undefined && key !== 'phoneRing') {
        const handleEnded = () => {
          audio.volume = originalVolume;
          audio.removeEventListener('ended', handleEnded);
        };
        audio.addEventListener('ended', handleEnded);
      }
      // phoneRing volume stays at reduced level - no restoration
    } catch (error: any) {
      // Handle autoplay restrictions
      if (error.name === 'NotAllowedError') {
        console.warn(`[AudioNotification] 🚫 AUTOPLAY BLOCKED for ${key}!`);
        console.warn(`[AudioNotification] User interaction required before audio can play.`);
        console.warn(`[AudioNotification] Have the user click anywhere on the page to enable audio.`);
      } else {
        console.error(`[AudioNotification] ❌ Error playing ${key}:`, error.name, error.message);
        console.error(`[AudioNotification] Full error:`, error);
      }
    }
  }

  /**
   * Play notification for new pending call
   */
  public playNewWaitingIncident(): void {
    this.playAudio('newWaitingIncident');
  }

  /**
   * Play notification when user's unit is assigned to a call
   */
  public playCallAssignedToMe(): void {
    this.playAudio('callAssignedToMe');
  }

  /**
   * Play phone ring notification for call updates
   */
  public playPhoneRing(): void {
    // Set phone ring volume to 50%
    this.playAudio('phoneRing', 0.5);
  }

  /**
   * Play medium tone notification
   */
  public playMediumTone(): void {
    this.playAudio('mediumTone');
  }

  /**
   * Play priority high notification
   */
  public playPriorityHigh(): void {
    this.playAudio('priorityHigh');
  }

  /**
   * Play long tone notification
   */
  public playLongTone(): void {
    this.playAudio('longTone');
  }

  /**
   * Set the volume for all notifications (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.audioCache.forEach(audio => {
      audio.volume = clampedVolume;
    });
  }

  /**
   * Enable or disable audio notifications
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`[AudioNotification] Notifications ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get current enabled state
   */
  public getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Test audio playback (call this on user interaction to prime audio contexts)
   */
  public async testPlayback(): Promise<void> {
    console.log('[AudioNotification] Testing audio playback...');
    const audio = this.audioCache.get('mediumTone');
    if (audio) {
      try {
        audio.volume = 0.1; // Very quiet for test
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0.7; // Reset to default
        console.log('[AudioNotification] Test playback successful');
      } catch (error) {
        console.warn('[AudioNotification] Test playback failed:', error);
      }
    }
  }
}

// Export singleton instance
export const audioNotifications = new AudioNotificationService();

